import React, { useEffect, useState } from "react";
import { CreatePostApi, MediaTypes, PostTypes } from "../api/Items";
import { useCreateItemPost } from "../api/ItemsApi";
import { ReportSummaryItemApi } from "../api/Reports";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import { DropdownItem } from "../shared-components/DropdownCombo";
import InputField from "../shared-components/InputField";
import LabeledCheckbox from "../shared-components/LabeledCheckbox";
import TextAreaField from "../shared-components/TextAreaField";
import { imageTypeInURL, validURL } from "../utils";
import { useReportDetail } from "../reports/ReportDetailProvider";
import DropdownComboSimple from "../shared-components/DropdownComboSimple";
import { behaviorMap, useFamaFlags } from "../api/FamaFlags";

import { DocumentPlusIcon } from "@heroicons/react/24/outline";

export interface OnCloseParams {
  post: CreatePostApi;
  itemUUID: string;
}

type AddPostProps = {
  report?: ReportSummaryItemApi;
  behaviors?: string[];
  onClose: (createdPost?: OnCloseParams) => void;
};

type formErrors = {
  profile: boolean;
  url: boolean;
  imageUrl: boolean;
  type: boolean;
  text: boolean;
  date: boolean;
  ratings: boolean;
  submissionTried: boolean;
};

const AddPost = ({ report, onClose }: AddPostProps) => {
  const postTypeOptions = Object.values(PostTypes).map((value, index) => {
    return { id: index.toString(), label: value };
  });
  const { behaviorMappingActions } = useFamaFlags();
  const [behaviourOptions, setBehaviourOptions] = useState<DropdownItem[]>([]);

  const [post, setPost] = useState<CreatePostApi>({
    report_uuid: report?.report_uuid ?? "",
    profile_uuid: "",
    profile_url: "",
    url: "",
    created_at: "",
    type: undefined,
    text: "",
    site_id: "",
    ratings: [],
    media: [],
  });

  const [errors, setErrors] = useState<formErrors>({
    profile: false,
    url: false,
    imageUrl: false,
    type: false,
    text: false,
    date: false,
    ratings: false,
    submissionTried: false,
  });

  const {
    mutate: createPost,
    isLoading: isLoadingPostCreating,
    error: postCreationError,
    isSuccess: successPost,
    data: createdPostUUID,
    reset: resetCreatePost,
  } = useCreateItemPost();

  const { profileList, isLoadingProfiles, blueprint, isLoadingBlueprint } =
    useReportDetail();

  useEffect(() => {
    if (successPost && createdPostUUID) {
      onClose({ post, itemUUID: createdPostUUID });
      resetCreatePost();
    }
  }, [
    onClose,
    successPost,
    postCreationError,
    createdPostUUID,
    post,
    resetCreatePost,
  ]);

  useEffect(() => {
    if (blueprint?.behaviors) {
      const newBehaviors: string[] = [];
      blueprint.behaviors.forEach((behavior) => {
        if (!newBehaviors.includes(behavior)) {
          newBehaviors.push(behavior);
        }
      });
      const newBehaviorOptions = newBehaviors.map((value, index) => {
        return { id: index.toString(), label: value };
      });
      setBehaviourOptions(newBehaviorOptions);
    }
  }, [blueprint?.behaviors]);

  useEffect(() => {
    // Form Validation
    const profileError =
      post.profile_url.length === 0 || post.profile_uuid.length === 0;
    const textError = post.text.length === 0;
    const typeError = !post.type;
    const urlError = !validURL(post.url);
    const imageError =
      post.media.length !== 0 &&
      (!validURL(post.media[0]["external_url"]) ||
        !imageTypeInURL(post.media[0]["external_url"]));
    const ratingError = post.ratings.length === 0;
    const dateError =
      post.created_at.length === 0 || new Date() < new Date(post.created_at);

    setErrors((errors) => ({
      ...errors,
      profile: profileError,
      url: urlError,
      imageUrl: imageError,
      ratings: ratingError,
      date: dateError,
      text: textError,
      type: typeError,
    }));
  }, [setErrors, post]);

  const profileOptions = () => {
    if (profileList) {
      return profileList.map((value) => {
        return {
          label: (value.item.content?.url as string) ?? "",
          id: value.item.external_id,
        };
      });
    }
    return [];
  };

  const onRatingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRating = e.target.name;

    // If checked is removed, remove from Set
    if (!e.target.checked) {
      const newRatings = post.ratings.filter((rating) => {
        return rating.name !== newRating;
      });
      setPost({ ...post, ratings: newRatings });
    } else {
      const newRatings = post.ratings;
      newRatings.push({ name: newRating, score_type: "behavior" });
      setPost({ ...post, ratings: newRatings });
    }
  };

  const handleSavePost = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setErrors({ ...errors, submissionTried: true });

    if (
      !errors.url &&
      !errors.profile &&
      !errors.imageUrl &&
      !errors.text &&
      !errors.ratings &&
      !errors.date &&
      !errors.type
    )
      createPost(post);
  };

  const showError = (): boolean => {
    const formError =
      errors.profile ||
      errors.url ||
      errors.imageUrl ||
      errors.ratings ||
      errors.date ||
      errors.text ||
      errors.type;
    return errors.submissionTried && (formError || !!postCreationError);
  };

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  return (
    <div className="flex gap-4">
      <div>
        <div className="bg-green-200 text-green-600 p-2 rounded-full">
          <DocumentPlusIcon className="w-5" />
        </div>
      </div>
      <div className="flex-1">
        <span className="text-xl block pb-4">Add Post</span>

        <form>
          <DropdownComboSimple
            items={profileOptions()}
            onChange={(e) => {
              setPost({ ...post, profile_url: e.label, profile_uuid: e.id });
            }}
            label={"Select Profile *"}
            name="selected-profile"
            labelClassName="font-bold"
            buttonContainerClassName="w-full relative p-2 pl-1 cursor-default overflow-hidden rounded-lg border bg-white text-left shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-300 sm:text-sm"
            placeHolder={isLoadingProfiles ? "Loading" : "Select Profile"}
            containerClassName="pb-4"
          ></DropdownComboSimple>

          <InputField
            classes="w-full"
            className="w-full mb-4 mr-8"
            handleChange={(e) => {
              setPost({ ...post, url: e.target.value });
            }}
            name={"URL"}
            placeholder="Type here..."
            label={"URL *"}
            labelClassName="font-bold"
          />
          <InputField
            classes="w-full"
            className="w-full mb-4 mr-8"
            handleChange={(e) => {
              setPost({
                ...post,
                created_at: new Date(e.target.value)
                  .toISOString()
                  .split("T")[0],
              });
            }}
            name={"Date"}
            placeholder="Select"
            label={"Date *"}
            type="date"
            labelClassName="font-bold"
          />

          <DropdownComboSimple
            items={postTypeOptions}
            onChange={(e) => {
              setPost({ ...post, type: e.label as PostTypes });
            }}
            label={"Post Type *"}
            name="selected-post-type"
            labelClassName="font-bold"
            containerClassName="capitalize pb-4"
            placeHolder="Select"
            buttonContainerClassName="w-full relative p-2 pl-1 cursor-default overflow-hidden rounded-lg border bg-white text-left shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-300 sm:text-sm"
          ></DropdownComboSimple>

          <TextAreaField
            classes="w-full pb-4"
            handleChange={(e) => {
              setPost({ ...post, text: e.target.value });
            }}
            name={"Text"}
            placeholder="Type here..."
            label={"Text *"}
            type="text"
            className="h-28 w-full"
            labelClassName="font-bold"
          />

          <div className="flex justify-between">
            <InputField
              classes="w-full pb-4"
              className="w-full"
              handleChange={(e) => {
                setPost({
                  ...post,
                  media:
                    e.target.value.length > 0
                      ? [
                          {
                            external_url: e.target.value,
                            type: MediaTypes.Image,
                          },
                        ]
                      : [],
                });
              }}
              name={"Image URL"}
              placeholder="Type here..."
              label={"Image URL"}
              labelClassName="font-bold"
            />
          </div>

          <div className="grid grid-cols-2 border border-1 border-gray-300 rounded-md">
            {behaviourOptions.map((behaviour) => (
              <LabeledCheckbox
                name={behaviour.label}
                label={behaviorMap(
                  behaviour.label,
                  behaviorMappingActions,
                  "key"
                )}
                classes="flex-grow w-3/12 m-2"
                onChange={onRatingChange}
                key={behaviour.id}
              ></LabeledCheckbox>
            ))}
          </div>

          {showError() && (
            <div className="p-4 my-4 bg-red-300 text-red-900 rounded-lg">
              <ul>
                {errors.profile && <li>Profile is a required field.</li>}
                {errors.url && (
                  <li>URL is a required field and should be a valid URL.</li>
                )}
                {errors.date && (
                  <li>Date is a required field and should be in the past.</li>
                )}
                {errors.type && <li>Post type is a required field. </li>}
                {errors.text && <li>Text is a required field.</li>}
                {errors.imageUrl && <li>ImageURL is not a valid URL.</li>}
                {errors.ratings && (
                  <li>At least one behavior should be selected.</li>
                )}
                {!successPost && !!postCreationError && (
                  <li>
                    Make sure the URL is unique and there is no other posts with
                    it, if problem persist contact support.
                  </li>
                )}
              </ul>
            </div>
          )}
        </form>

        <div className="flex items-center justify-end pt-4 gap-3">
          <Button
            type={ButtonTypes.default}
            text="Cancel"
            colorType={ButtonColorTypes.cancel}
            onClick={handleClose}
          />

          <Button
            disabled={
              isLoadingPostCreating || isLoadingBlueprint || isLoadingProfiles
            }
            type={
              isLoadingPostCreating ? ButtonTypes.saving : ButtonTypes.default
            }
            text="Save"
            onClick={handleSavePost}
            colorType={ButtonColorTypes.primary}
          />
        </div>
      </div>
    </div>
  );
};

export default AddPost;
